import { styled } from "@linaria/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"


import { ClickableContextItemPill, ContextItemPill, ContextItemPillRemove } from "@/components/Chat/ContextItemPill.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useQueries } from "@/hooks/useRootContext.ts"
import {
  CloseLight,
  ErrorFill,
  FilePlainLight,
  FilePlainRegular,
  FolderLight,
  Icon16,
  Icon20
} from "@/icons/Icons.ts"
import { displayName, displayPath } from "@/lib/displayUtils.ts"
import { isImageFile } from "@/lib/std.ts"
import { useTaskMatch } from "@/routes/tasks/tasksHooks.ts"
import { ellipsis, focusOutline } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { AiAgentTaskContextItem, AiChatMessageAttachment, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { ReactNode } from "react"

export type ResolvedAttachment = {
  status: "pending" | "resolved" | "failed"
  promise: Promise<AiChatMessageAttachment>
  result?: AiChatMessageAttachment
  error?: Error
  blob?: Blob
}

// TODO "Remove" action should remove the uploaded file from storage
// eslint-disable-next-line react/function-component-definition
export function Attachments<DomElement>(props: {
  attachments: ResolvedAttachment[]
  contextItems?: AiAgentTaskContextItem[]
  domElements?: DomElement[],
  domElementBuilder?: (element: DomElement, index: number) => ReactNode
  showPreviewTooltips: boolean
  allowDownload: boolean
  onDownload: (attachment: AiChatMessageAttachment) => void
  onRemoveAttachment?: (attachment: AiChatMessageAttachment) => void
  onRemoveFailedAttachment?: (index: number) => void
  onRemoveContextItem?: (contextItem: AiAgentTaskContextItem) => void
}) {
  const attachments = props.attachments

  // Rendered as pills: icon + name + remove button
  const pillAttachments = useMemo(() => {
    return attachments.filter(attachment => {
      if (attachment.status === "pending" || attachment.status === "failed") {
        return !attachment.blob || !attachment.blob.type.startsWith("image/")
      }
      if (attachment.result) {
        return !isImageFile(attachment.result.filename)
      }
      return false
    })
  }, [attachments])

  // Rendered as image thumbnails + remove button
  const imageAttachments = useMemo(() => {
    return attachments.filter(attachment => {
      if (attachment.status === "pending" || attachment.status === "failed") {
        return attachment.blob && attachment.blob.type.startsWith("image/")
      }
      if (attachment.status === "resolved" && attachment.result) {
        return isImageFile(attachment.result.filename)
      }
      return false
    })
  }, [attachments])

  const contextItems = props.contextItems || []
  const domElements = props.domElements || []

  const hasContextOrPillAttachments = pillAttachments.length > 0 || contextItems.length > 0 || domElements.length > 0

  return (
    <AttachmentsContainer>
      {hasContextOrPillAttachments && (
        <AttachmentRow>
          {domElements?.map((element, index) => props.domElementBuilder?.(element, index))}

          {contextItems.map(item => (
            <ContextItemPill key={item.name} withRemoveIcon={!!props.onRemoveContextItem}>
              {item.type === "DIR"
                ? <FolderLight className={Icon20} />
                : <FilePlainLight className={Icon20} />
              }

              <Tooltip content={displayPath(item.name)}>
                <EllipsisContainer>
                  <Ellipsis>{displayName(item.name)}</Ellipsis>
                </EllipsisContainer>
              </Tooltip>

              {props.onRemoveContextItem && (
                <a href="#" onClick={(e) => {
                  e.preventDefault()
                  props.onRemoveContextItem!(item)
                }}>
                  <Tooltip content="Remove"><ContextItemPillRemove /></Tooltip>
                </a>
              )}
            </ContextItemPill>
          ))}

          {pillAttachments.map((attachment, index) => {
            if (attachment.status === "pending") {
              return (
                <ContextItemPill key={index} withRemoveIcon={false}>
                  <Spinner text={ellipsize("Upload in progress")} size={20} />
                </ContextItemPill>
              )
            } else if (attachment.status === "failed") {
              return (
                <Tooltip key={index} content={attachment.error?.message ?? "Unknown error"}>
                  <ContextItemPill withRemoveIcon={!!props.onRemoveFailedAttachment}>
                    <ErrorFill className={Icon20} color="var(--error-color)" />
                    Upload failed
                    {props.onRemoveFailedAttachment && (
                      <a href="#" onClick={(e) => {
                        e.preventDefault()
                        props.onRemoveFailedAttachment!(index)
                      }}>
                        <Tooltip content="Remove"><ContextItemPillRemove /></Tooltip>
                      </a>
                    )}
                  </ContextItemPill>
                </Tooltip>
              )
            } else if (attachment.result) {
              const result = attachment.result
              return (
                <Attachment
                  key={result.filename}
                  tabIndex={props.allowDownload ? 0 : undefined}
                  onClick={props.allowDownload ? (e) => {
                    e.preventDefault()
                    props.onDownload(result)
                  } : undefined}
                >
                  <AttachmentThumbnail
                    attachment={result}
                    blob={attachment.blob}
                    showPreviewTooltips={props.showPreviewTooltips}
                    allowDownload={props.allowDownload}
                    onRemove={props.onRemoveAttachment}
                  />
                </Attachment>
              )
            }
          })}
        </AttachmentRow>
      )}

      {imageAttachments.length > 0 && (
        <AttachmentRow>
          {imageAttachments.map((attachment, index) => {
            if (attachment.status === "pending") {
              return (
                <ImageThumbnail key={index} clickable={false}>
                  <ImagePreviewLoading>
                    <Spinner size={20} />
                  </ImagePreviewLoading>
                </ImageThumbnail>
              )
            } else if (attachment.status === "failed") {
              return (
                <ImageThumbnail key={index} clickable={false}>
                  <ImagePreviewFailed>
                    <ErrorFill className={Icon20} color="var(--error-color)" />
                  </ImagePreviewFailed>
                  {props.onRemoveFailedAttachment && (
                    <ImagePreviewRemoveButton
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        props.onRemoveFailedAttachment!(index)
                      }}
                    >
                      <Tooltip content="Remove">
                        <CloseLight className={Icon16} />
                      </Tooltip>
                    </ImagePreviewRemoveButton>
                  )}
                </ImageThumbnail>
              )
            } else if (attachment.result) {
              const result = attachment.result
              return (
                <AttachmentThumbnail
                  key={result.filename}
                  attachment={result}
                  blob={attachment.blob}
                  showPreviewTooltips={props.showPreviewTooltips}
                  allowDownload={props.allowDownload}
                  onRemove={props.onRemoveAttachment}
                />
              )
            }
          })}
        </AttachmentRow>
      )}
    </AttachmentsContainer>
  )
}

const AttachmentThumbnail = (props: {
  attachment: AiChatMessageAttachment
  blob?: Blob
  showPreviewTooltips: boolean
  allowDownload: boolean
  onRemove: ((attachment: AiChatMessageAttachment) => void) | undefined
}) => {
  const isImage = isImageFile(props.attachment.filename)

  if (isImage) {
    return (
      <ImageAttachmentPreview
        attachment={props.attachment}
        blob={props.blob}
        onRemove={props.onRemove}
        allowDownload={props.allowDownload}
      />
    )
  }

  if (props.allowDownload) {
    if (props.showPreviewTooltips) {
      return (
        <Tooltip content={<AttachmentPreview attachment={props.attachment} />}>
          <ClickableContextItemPill withRemoveIcon={props.onRemove !== undefined}>
            <AttachmentPillContent attachment={props.attachment} onRemove={props.onRemove} />
          </ClickableContextItemPill>
        </Tooltip>
      )
    } else {
      return (
        <ClickableContextItemPill withRemoveIcon={props.onRemove !== undefined}>
          <AttachmentPillContent attachment={props.attachment} onRemove={props.onRemove} />
        </ClickableContextItemPill>
      )
    }
  } else {
    if (props.showPreviewTooltips) {
      return (
        <Tooltip content={<AttachmentPreview attachment={props.attachment} />}>
          <ContextItemPill withRemoveIcon={props.onRemove !== undefined}>
            <AttachmentPillContent attachment={props.attachment} onRemove={props.onRemove} />
          </ContextItemPill>
        </Tooltip>
      )
    } else {
      return (
        <ContextItemPill withRemoveIcon={props.onRemove !== undefined}>
          <AttachmentPillContent attachment={props.attachment} onRemove={props.onRemove} />
        </ContextItemPill>
      )
    }
  }
}

const AttachmentPillContent = (props: {
  attachment: AiChatMessageAttachment
  onRemove: ((attachment: AiChatMessageAttachment) => void) | undefined
}) => {
  return (
    <>
      <FilePlainRegular className={Icon20} />
      <EllipsisContainer>
        <Ellipsis>{props.attachment.filename}</Ellipsis>
      </EllipsisContainer>
      {props.onRemove && (
        <a href="#" onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          props.onRemove?.(props.attachment)
        }}>
          <Tooltip content="Remove"><ContextItemPillRemove /></Tooltip>
        </a>
      )}
    </>
  )
}

const ImageAttachmentPreview = (props: {
  attachment: AiChatMessageAttachment
  blob?: Blob
  onRemove: ((attachment: AiChatMessageAttachment) => void) | undefined
  allowDownload: boolean
}) => {
  const client = useQueries().apiClient
  const taskMatch = useTaskMatch()
  const taskId = taskMatch?.params?.taskId

  const downloadAttachment = useQuery({
    queryKey: ["downloadAttachment", props.attachment.id, taskId],
    queryFn: () => {
      if (taskId) {
        return client.downloadAttachment(taskId as TID, props.attachment.id)
      }
      throw new Error("Task ID is not available")
    },
    enabled: !!taskId && !props.blob
  })

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (props.blob) {
      const url = URL.createObjectURL(props.blob)
      setImageUrl(url)
      setImageError(false)

      return () => {
        URL.revokeObjectURL(url)
      }
    } else if (downloadAttachment.data) {
      const url = URL.createObjectURL(downloadAttachment.data)
      setImageUrl(url)
      setImageError(false)

      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [props.blob, downloadAttachment.data])

  const handleClick = () => {
    if (imageUrl) {
      setIsModalOpen(true)
    }
  }

  return (
    <>
      <ImageThumbnail
        data-testid={props.attachment.filename}
        onClick={handleClick}
        clickable={!!imageUrl}
      >
        {downloadAttachment.isLoading && !props.blob && (
          <ImagePreviewLoading>
            <Spinner size={20} />
          </ImagePreviewLoading>
        )}

        {imageUrl && !imageError && (
          <ImagePreview
            src={imageUrl}
            alt={props.attachment.filename}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        )}

        {(imageError || (!downloadAttachment.isLoading && !imageUrl && !props.blob)) && (
          <ImagePreviewError>
            <FilePlainRegular className={Icon20} />
            <EllipsisContainer>
              <ImagePreviewFilename title={props.attachment.filename}>{props.attachment.filename}</ImagePreviewFilename>
            </EllipsisContainer>
          </ImagePreviewError>
        )}

        {props.onRemove && (
          <ImagePreviewRemoveButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              props.onRemove?.(props.attachment)
            }}
          >
            <Tooltip content="Remove">
              <CloseLight className={Icon16} />
            </Tooltip>
          </ImagePreviewRemoveButton>
        )}
      </ImageThumbnail>

      {imageUrl && (
        <Dialog
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          trigger={null}
          title={null}
        >
          <Vertical alignItems="center" gap="8px">
            <ImageInDialog src={imageUrl} alt={props.attachment.filename} />
            <EllipsisContainer><ImageFilename>{props.attachment.filename}</ImageFilename></EllipsisContainer>
          </Vertical>
        </Dialog>
      )}
    </>
  )
}

const AttachmentPreview = ({ attachment }: { attachment: AiChatMessageAttachment }) => {
  const client = useQueries().apiClient

  const taskMatch = useTaskMatch()
  const taskId = taskMatch?.params?.taskId

  const downloadAttachment = useQuery({
    queryKey: ["downloadAttachment", attachment.id, taskId],
    queryFn: () => {
      if (taskId) {
        return client.downloadAttachment(taskId as TID, attachment.id)
      }
      throw new Error("Task ID is not available")
    },
    enabled: !!taskId
  })

  const data = downloadAttachment.data

  if (data) {
    if (data.type.startsWith("text")) {
      return <TextAttachmentPreview blob={data} />
    } else if (data.type.startsWith("image")) {
      return <ImageAttachmentTooltipPreview blob={data} />
    } else {
      return "No preview available"
    }
  } else if (downloadAttachment.isLoading) {
    return <Spinner text={ellipsize("Loading preview")} />
  } else {
    return "No preview available"
  }
}

const TextAttachmentPreview = ({ blob }: { blob: Blob }) => {
  const [text, setText] = useState<string | null>(null)

  useEffect(() => {
    const reader = new FileReader()
    reader.onload = () => {
      setText(reader.result as string)
    }
    reader.readAsText(blob)
  }, [blob])

  return (
    <TextAttachmentScrollable>
      <TextAttachment>{text}</TextAttachment>
    </TextAttachmentScrollable>
  )
}

const ImageAttachmentTooltipPreview = ({ blob }: { blob: Blob }) => {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [blob])

  return url ? <ImageAttachment src={url} alt="Image preview" /> : null
}

const TextAttachmentScrollable = styled(Scrollable)`
  max-width: 50vw;
  max-height: 50vh;
`

const TextAttachment = styled.pre`
  ${textStyles.pre};
`

const ImageAttachment = styled.img`
  display: block;
  max-width: 50vw;
  max-height: 50vh;
`

const Attachment = styled.span`
  &:focus-visible {
    border-radius: var(--border-radius-16);

    ${focusOutline};
  }
`

const ImageThumbnail = styled(Horizontal)<{ clickable: boolean }>`
  align-items: center;
  justify-content: center;

  overflow: hidden;
  position: relative;

  width: 120px;
  height: 120px;

  border: 1px solid var(--text-color-10);
  border-radius: var(--border-radius-12);

  background-color: var(--text-color-2);

  cursor: ${props => props.clickable ? "pointer" : "default"};

  &:hover {
    ${props => props.clickable ? "background-color: var(--text-color-5);" : ""}
  }

  &:focus-visible {
    ${focusOutline};
  }
`

const ImagePreviewLoading = styled(Horizontal)`
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`

const ImagePreviewFailed = styled(Horizontal)`
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ImagePreviewError = styled(Vertical)`
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 12px;

  color: var(--secondary-text-color);
  text-align: center;
`

const ImagePreviewFilename = styled.div`
  ${textStyles.smallestFontSize};
  ${ellipsis};
`

const ImagePreviewRemoveButton = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;

  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--background-color-40);
  border-radius: var(--border-radius-round);
  border: none;

  color: var(--text-color-60);
  cursor: pointer;

  backdrop-filter: blur(4px);

  opacity: 0;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: var(--text-color);
    background-color: var(--background-color-30);
  }

  &:focus-visible {
    opacity: 1;

    ${focusOutline};
  }

  ${ImageThumbnail}:hover & {
    opacity: 1;
  }
`

const AttachmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AttachmentRow = styled(Horizontal)`
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 4px;
`

const ImageInDialog = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 64px);
  object-fit: contain;
`

const ImageFilename = styled(SecondaryTextSpan)`
  ${ellipsis};
`
